import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import SearchIcon from "@material-ui/icons/Search";
import SettingsIcon from "@material-ui/icons/Settings";

import { StyledTableCell, useInvoiceStyles } from "../styles";
import StatusChip from "./StatusChip";

const COLUMNS = [
  "#",
  "Invoice No.",
  "Client",
  "Email",
  "Bp",
  "Event",
  "Date",
  "With Image",
  "Payment",
  "Status",
  "Action",
];

const InvoiceHistoryTable = ({
  rows,
  eventId,
  eventName,
  search,
  onSearchChange,
  loading,
  imageOptions,
  onImageChange,
  onSendAll,
  onPreview,
  onSend,
  onOpenPayment,
  onOpenMenu,
}) => {
  const classes = useInvoiceStyles();

  return (
    <Paper className={classes.tableCard}>
      {/* ============================================================
          HEADER
      ============================================================ */}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2.5}
        py={2}
      >
        <Box>
          <Typography variant="h6" className={classes.tableTitle}>
            Invoice History
          </Typography>

          <Typography variant="body2" className={classes.tableSubtitle}>
            View generated invoices and check their email status
          </Typography>
        </Box>
      </Box>

      {/* ============================================================
          SEARCH / ACTIONS
      ============================================================ */}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2.5}
        py={2}
      >
        <Box>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search invoices..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            style={{
              width: 280,
            }}
          />
        </Box>

        <Box
          display="flex"
          alignItems="center"
          style={{
            gap: 12,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={onSendAll}
            disabled={!rows || rows.length === 0}
            style={{
              height: 40,
              minWidth: 110,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Send All
          </Button>
        </Box>
      </Box>

      <Divider />

      {/* ============================================================
          TABLE
      ============================================================ */}

      <TableContainer className={classes.tableContainer}>
        <Table stickyHeader className={classes.table}>
          <TableHead>
            <TableRow>
              {COLUMNS.map((label, index) => (
                <StyledTableCell
                  key={label}
                  align={index >= 4 ? "center" : "inherit"}
                >
                  {label}
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows && rows.length > 0 ? (
              rows.map((row, index) => {
                const invoiceStatus = String(
                  row.status || "DRAFT"
                ).toUpperCase();

                const includeImage =
                  imageOptions[row.id] !== undefined
                    ? imageOptions[row.id]
                    : Boolean(
                        row.with_image !== undefined
                          ? row.with_image
                          : true
                      );

                const buyerPremium =
                  row.buyer_premium !== undefined &&
                  row.buyer_premium !== null &&
                  row.buyer_premium !== ""
                    ? Number(row.buyer_premium)
                    : 0;

                return (
                  <TableRow
                    hover
                    key={row.id || index}
                    className={classes.tableRow}
                  >
                    {/* # */}
                    <StyledTableCell>
                      {index + 1}
                    </StyledTableCell>

                    {/* INVOICE NO */}
                    <StyledTableCell>
                      <Typography
                        style={{
                          fontWeight: 600,
                          color: "#222",
                          fontSize: 13,
                        }}
                      >
                        {row.invoice_no ||
                          row.invoice_number ||
                          row.receipt_number ||
                          "---"}
                      </Typography>
                    </StyledTableCell>

                    {/* CLIENT */}
                    <StyledTableCell>
                      <Typography className={classes.clientName}>
                        {row.name ||
                          row.customer_name ||
                          row.client_name ||
                          "---"}
                      </Typography>
                    </StyledTableCell>

                    {/* EMAIL */}
                    <StyledTableCell>
                      <Typography className={classes.email}>
                        {row.email ||
                          row.customer_email ||
                          "---"}
                      </Typography>
                    </StyledTableCell>

                    {/* BUYER PREMIUM */}
                    <StyledTableCell align="center">
                      <Typography
                        style={{
                          fontWeight: 600,
                          fontSize: 13,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {buyerPremium * 100}%
                      </Typography>
                    </StyledTableCell>

                    {/* EVENT */}
                    <StyledTableCell>
                      {row.event_name ||
                        row.auction_name ||
                        eventName ||
                        "---"}
                    </StyledTableCell>

                    {/* DATE */}
                    <StyledTableCell>
                      {row.created_at
                        ? new Date(
                            row.created_at
                          ).toLocaleString()
                        : "---"}
                    </StyledTableCell>

                    {/* WITH IMAGE */}
                    <StyledTableCell align="center">
                      <Checkbox
                        color="primary"
                        size="small"
                        checked={includeImage}
                        onChange={(e) =>
                          onImageChange(
                            row.id,
                            e.target.checked
                          )
                        }
                      />
                    </StyledTableCell>

                    {/* PAYMENT */}
                    <StyledTableCell align="center">
                      <StatusChip
                        kind="payment"
                        value={row.payment_status}
                        onClick={() => onOpenPayment(row)}
                      />
                    </StyledTableCell>

                    {/* STATUS */}
                    <StyledTableCell align="center">
                      <StatusChip
                        kind="status"
                        value={row.status}
                      />
                    </StyledTableCell>

                    {/* ACTION */}
                    <StyledTableCell align="center">
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        style={{
                          gap: 8,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Button
                          className={classes.actionButton}
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() =>
                            onPreview(
                              row,
                              includeImage
                            )
                          }
                        >
                          Preview
                        </Button>

                        {invoiceStatus !== "SENT" &&
                          invoiceStatus !== "CANCELLED" && (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={() =>
                                onSend(row)
                              }
                              disabled={loading}
                            >
                              Send
                            </Button>
                          )}

                        <IconButton
                          size="small"
                          onClick={(e) =>
                            onOpenMenu(e, row)
                          }
                          title="Options"
                        >
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </StyledTableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <StyledTableCell
                  colSpan={COLUMNS.length}
                  align="center"
                >
                  <Box className={classes.emptyState}>
                    {eventId
                      ? "No invoice records found"
                      : "Select an auction to view invoice history"}
                  </Box>
                </StyledTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

InvoiceHistoryTable.propTypes = {
  rows: PropTypes.array,
  eventId: PropTypes.any,
  eventName: PropTypes.string,
  search: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  imageOptions: PropTypes.object,
  onImageChange: PropTypes.func.isRequired,
  onSendAll: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onOpenPayment: PropTypes.func.isRequired,
  onOpenMenu: PropTypes.func.isRequired,
};

InvoiceHistoryTable.defaultProps = {
  rows: [],
  eventName: "",
  search: "",
  loading: false,
  imageOptions: {},
};

export default InvoiceHistoryTable;